import {IFeatureLayerInfo} from '../../interfaces/models'
import {GeometryType} from '../../enums/GeometryType'
import {Geometry, Point, LineString, MultiPolygon, Polygon} from '../../models'
import {CoordinateSystem} from '../../enums/CoordinateSystem'
import {ServiceHub} from '../../ServiceHub'
import {getMapService} from '../../reducers/map'

export class GeometryStringConverter {
  protected multiPartStringSeparator: string
  protected multiHoleStringSeparator?: string
  constructor(multiPartStringSeparator: string, multiHoleStringSeparator?: string) {
    this.multiPartStringSeparator = multiPartStringSeparator
    this.multiHoleStringSeparator = multiHoleStringSeparator
  }

  getGeometryAsStringAsync(
    coordinateSystem: CoordinateSystem,
    geometryType: GeometryType,
    geometry?: Geometry
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const geometryString = geometryType == GeometryType.Point ? ';' : ''
      if (geometry) {
        ServiceHub.BgTransService.bgTrans(coordinateSystem, geometry).then(geom => {
          resolve(this.getGeometryAsString(coordinateSystem, geometryType, geom))
        })
      } else {
        resolve(geometryString)
      }
    })
  }

  public getGeometryAsString(
    coordinateSystem: CoordinateSystem,
    geometryType: GeometryType,
    geometry?: Geometry
  ): string {
    let geometryString = geometryType == GeometryType.Point ? ';' : ''
    const fixed = coordinateSystem != CoordinateSystem.GEOGRAPHIC ? 3 : 10
    if (geometry) {
      const coordinates = geometry.getCoordinates()
      if (geometryType == GeometryType.Point) {
        if (!isNaN(coordinates[1] as number) && !isNaN(coordinates[0] as number)) {
          geometryString = `${(coordinates[1] as number).toFixed(7)};${(coordinates[0] as number).toFixed(7)}`
        }
      } else if (geometryType == GeometryType.Polygon) {
        const coords = coordinates as [number, number][][]
        const coordArray: string[] = []
        coords.forEach((ring, ringIndex) => {
          if (ring.length == 0) {
            coordArray.push(this.multiHoleStringSeparator || '')
          }
          ring.forEach((p, i) => {
            let coord = `${p[1].toFixed(fixed)} ${p[0].toFixed(fixed)}`
            if (ringIndex > 0 && i == 0) coord = (this.multiHoleStringSeparator || '') + coord
            coordArray.push(coord)
          })
        })
        geometryString = coordArray.join('\n')
      } else if (geometryType == GeometryType.MultiPolygon) {
        const coords = coordinates as [number, number][][][]
        const coordArray: string[] = []
        coords.forEach((poly, i) => {
          if (i != 0) {
            coordArray.push(this.multiPartStringSeparator)
          }
          poly.forEach((ring, ringIndex) => {
            ring.forEach((p, i) => {
              if (ring.length == 0) {
                coordArray.push(this.multiHoleStringSeparator || '')
              }
              let coord = `${p[1].toFixed(fixed)} ${p[0].toFixed(fixed)}`
              if (ringIndex > 0 && i == 0) coord = (this.multiHoleStringSeparator || '') + coord
              coordArray.push(coord)
            })
          })
        })
        geometryString = coordArray.join('\n')
      } else if (geometryType == GeometryType.MultiLineString) {
        const coords = coordinates as [number, number][][]
        const coordArray: string[] = []
        coords.forEach(line => {
          line.forEach((p, i) => {
            coordArray.push(`${p[1].toFixed(fixed)} ${p[0].toFixed(fixed)}`)
          })
        })
        geometryString = coordArray.join('\n')
      } else if (geometryType == GeometryType.LineString) {
        const coords = coordinates as [number, number][]
        const coordArray: string[] = []
        coords.forEach(p => {
          coordArray.push(`${p[1].toFixed(fixed)} ${p[0].toFixed(fixed)}`)
        })
        geometryString = coordArray.join('\n')
      } else {
        throw new Error(geometryType + ' Not implemented yet')
      }
    }
    return geometryString
  }

  public tryGetGeometryFromString(
    cs: CoordinateSystem,
    geometryType: GeometryType,
    geometryString: string
  ): Promise<Geometry | undefined> {
    return new Promise((resolve, reject) => {
      const resolveGeom = (geom: Geometry) => {
        geom.cs = cs
        ServiceHub.BgTransService.bgTrans(getMapService().getCode(), geom)
          .then(g => {
            resolve(g)
          })
          .catch(e => {
            resolve(undefined)
          })
      }
      if (geometryString && this.getGeomStringErrors(geometryType, geometryString).length == 0) {
        if (geometryType == GeometryType.Point) {
          const geometrySplit = geometryString.split(';')
          const x = parseFloat(geometrySplit[0])
          const y = parseFloat(geometrySplit[1])
          if (geometrySplit[0] && geometrySplit[1] && !isNaN(x) && !isNaN(y)) {
            const geom = new Point([y, x])
            resolveGeom(geom)
          } else {
            resolve(undefined)
          }
        } else if (geometryType == GeometryType.Polygon) {
          const pathCoordiantes: [number, number][][] = []
          let paths = [geometryString]
          if (this.multiHoleStringSeparator) {
            paths = geometryString.split(this.multiHoleStringSeparator)
          }
          paths.forEach(path => {
            const coordinates: [number, number][] = []
            const points = path.split('\n')
            for (let i = 0; i < points.length; i++) {
              const p = points[i]
              if (!p) {
                continue
              }
              const xy = p.replace(/\t/gi, ' ').replace(/,/gi, ' ').replace(/\s\s+/g, ' ').trim().split(' ')
              coordinates.push([parseFloat(xy[1]), parseFloat(xy[0])])
            }
            pathCoordiantes.push(coordinates)
          })

          if (pathCoordiantes.length > 0 && pathCoordiantes[0].length > 2) {
            const geom = new Polygon(pathCoordiantes)
            resolveGeom(geom)
          } else {
            resolve(undefined)
          }
        } else if (geometryType == GeometryType.LineString) {
          const coordinates: Array<[number, number]> = []
          const points = geometryString.split('\n')
          for (let i = 0; i < points.length; i++) {
            const p = points[i]
            if (!p) {
              continue
            }
            const xy = p.replace(/\t/gi, ' ').replace(/,/gi, ' ').replace(/\s\s+/g, ' ').trim().split(' ')
            coordinates.push([parseFloat(xy[1]), parseFloat(xy[0])])
          }
          if (coordinates.length > 1) {
            const geom = new LineString(coordinates)
            resolveGeom(geom)
          } else {
            resolve(undefined)
          }
        } else if (geometryType == GeometryType.MultiPolygon) {
          const partCoordinates: number[][][][] = []
          const parts = geometryString.split(this.multiPartStringSeparator)
          parts.forEach(part => {
            const polygon: number[][][] = []
            let paths = [geometryString]
            if (this.multiHoleStringSeparator) {
              paths = part.split(this.multiHoleStringSeparator)
            }
            paths.forEach(path => {
              const points = path.split('\n')
              const pathCoordiantes: number[][] = []
              for (let i = 0; i < points.length; i++) {
                const p = points[i]
                if (!p) {
                  continue
                }
                const xy = p.replace(/\t/gi, ' ').replace(/,/gi, ' ').replace(/\s\s+/g, ' ').trim().split(' ')
                pathCoordiantes.push([parseFloat(xy[1]), parseFloat(xy[0])])
              }
              polygon.push(pathCoordiantes)
            })
            partCoordinates.push(polygon)
          })
          if (partCoordinates.length > 0) {
            const geom = new MultiPolygon(partCoordinates)
            resolveGeom(geom)
          } else {
            resolve(undefined)
          }
        } else {
          throw new Error(`${geometryType} not implemented`)
        }
      } else {
        resolve(undefined)
      }
    })
  }
  public getGeomStringErrors(geometryType: GeometryType, geometryString: string): string[] {
    const errorLines: string[] = []
    if (geometryType == GeometryType.Polygon) {
      let paths = [geometryString]
      if (this.multiHoleStringSeparator) {
        paths = geometryString.split(this.multiHoleStringSeparator)
      }
      paths.forEach((path, pathIndex) => {
        const points = path.split('\n')
        for (let i = 0; i < points.length; i++) {
          const p = points[i]
          if (!p) {
            continue
          }
          const xy = p.replace(/\t/gi, ' ').replace(/,/gi, ' ').replace(/\s\s+/g, ' ').trim().split(' ')
          if (xy.length != 2) {
            errorLines.push(
              `Грешка в ред ${i + 1}${
                pathIndex > 0 ? `, Дупка № ${pathIndex}` : ''
              }. На ред може да има координати само за една точка.`
            )
          }
          const x = parseFloat(xy[0])
          const y = parseFloat(xy[1])
          if (isNaN(x) || isNaN(y)) {
            errorLines.push(
              `Грешка в ред ${i + 1}${pathIndex > 0 ? `, Дупка № ${pathIndex}` : ''}. Координатите не са валидни.`
            )
          }
        }
      })
    }

    return errorLines
  }
}
